<script lang="ts" setup>
    import { ref, watch, toRef, computed, watchEffect } from 'vue'
    import { useCompanyStore } from '@/stores/companyStore'
    import { useI18n } from 'vue-i18n'
    import { useApi } from '@/composables/useApi'

    const companyStore = useCompanyStore()
    const { t } = useI18n()

    type Employee = {
        employeeID: number
        fullName: string
    }

    const props = defineProps({
        modelValue: {
            type: [Number, null],
            default: null,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: null,
        },
        departmentId: {
            type: [Number, null],
            default: null,
        },
    })

    const emit = defineEmits(['update:modelValue'])
    const value = ref<number | null>(props.modelValue)

    const queryParams = ref({
        companyId: toRef(companyStore, 'companyID'),
        departmentId: toRef(props, 'departmentId'),
    })

    const { data: employees, isFetching: loading } = useApi<Employee, Employee[]>({
        endpoint: 'employee',
        queryParams: queryParams.value,
    })

    // Watch modelValue changes from parent
    watch(
        () => props.modelValue,
        newValue => {
            value.value = newValue
        },
    )

    // Watch local value changes
    watch(value, newValue => {
        emit('update:modelValue', newValue)
    })

    // Ensure selected employee exists in options
    const selectedEmployeeOption = ref<Employee | null>(null)
    watchEffect(() => {
        if (value.value != null && employees?.data) {
            selectedEmployeeOption.value = employees.data.find(e => e.employeeID === value.value) || null
        }
    })
</script>

<template>
    <el-form-item label-position="top" :label="$t('Select Employee')">
        <el-select
            size="large"
            filterable
            clearable
            :loading="loading"
            :disabled="disabled"
            class="w-100"
            v-model="value"
            :placeholder="placeholder || $t('Select Employee')"
        >
            <!-- Add the selected employee as option if not already in list -->
            <el-option
                v-if="selectedEmployeeOption && !employees?.data?.some(e => e.employeeID === value)"
                :key="selectedEmployeeOption.employeeID"
                :label="selectedEmployeeOption.fullName"
                :value="selectedEmployeeOption.employeeID"
            />

            <!-- Normal options -->
            <el-option
                v-for="employee in employees?.data || []"
                :key="employee.employeeID"
                :label="employee.fullName"
                :value="employee.employeeID"
            />
        </el-select>
    </el-form-item>
</template>
